<script lang="ts" setup>
import { inject, onMounted, ref, computed } from "vue";
import { useAgentSipConfigStore } from "~/stores/agentSipConfigStore";
import { InCall } from "~/types/enums/InCall";
import { CallEventType } from "~/types/callEventType";
import { useTasksStore } from "~/projects/task-manager/stores/tasksStore";
import { TaskableType } from "~/projects/task-manager/types/enums/TaskableType";
import { useLeadsStore } from "~/projects/crm/stores/leadsStore";
import { LeadAbleType } from "~/projects/crm/types/enums/leadAbleType";
import { CallOrigin } from "~/types/enums/callOrigin";
import { useTeleSalesStore } from "~/projects/tele-sales/stores/teleSalesStore";
import { useInboxStore } from "~/projects/help-desk/stores/inboxStore";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";
import CallService from "~/projects/cc/services/CallService";
import {
  formatDuration,
  getCleanedCustomerNumber,
  getCustomerUriInfo,
  getCustomerBasicInfo,
} from "~/projects/cc/utils/callCenterHelper";

import AddCustomer from "~/projects/cc/components/call/addCustomer.vue";
import { useCallHistoriesStore } from "~/projects/cc/stores/callHistorieStore";
import Timeout = NodeJS.Timeout;
import { useConfigStore } from "~/stores/configStore";
import { set } from "nuxt/dist/app/compat/capi";
import { useAutoDialerStore } from "~/projects/tele-sales/stores/autoDialerStore";

const configStore = useConfigStore();

const callHistoriesStore = useCallHistoriesStore();
const router = useRouter();
const tasksStore = useTasksStore();
const leadsStore = useLeadsStore();
const dialogStore = useDialogStore();
const sipConfigStore = useAgentSipConfigStore();
const inboxStore = useInboxStore();
type CallOptionName =
  | "Mute"
  | "Hold"
  | "CRM"
  | "SMS"
  | "Forward"
  | "Add Info"
  | "Add Notes"
  | "Add Task"
  | "Add Lead"
  | "Call Histories"
  | "Sale Info";

const selectedBtn = ref<CallOptionName | null>(null);
const callService = inject("callService") as CallService;

const isPaused = ref(false);
const isMuted = ref(false);
const showCallTime = ref(false);
const callHisId = ref(0);
const callStatus = ref("Idle");

const totalSeconds = computed(() => dialogStore.callDuration); // Use dialogStore for totalSeconds
let setIntervalID = null as Timeout | null;
const callerId = ref<string>("");
const callerQueue = ref<string | null>();
const lastCallSummary = ref("00:00");

const startCallTransfer = function () {
  dialogStore.getTransferableAgents();
  dialogStore.setTransferCallModalVisibility(true);
};

const dtmfKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const showDialpad = ref(false);
const dtmfLog = ref("");

const sendDtmf = function (tone: string) {
  if (dialogStore.inCall !== InCall.ONGOING) return;
  callService.sendDtmf(tone);
  dtmfLog.value = (dtmfLog.value + tone).slice(-16);
};

const openDialpad = function () {
  if (dialogStore.inCall !== InCall.ONGOING) return;
  showDialpad.value = true;
};

const closeDialpad = function () {
  showDialpad.value = false;
  dtmfLog.value = "";
};

const handleCallOption = function (option: CallOptionName) {
  if (selectedBtn.value === option) {
    selectedBtn.value = null;
  } else selectedBtn.value = option;
  switch (option) {
    case "Mute":
      callService.toggleMute();
      break;
    case "Hold":
      isPaused.value = callService.togglePause();
      break;
    case "CRM":
      // eslint-disable-next-line no-case-declarations
      const crmUrl = configStore.globalConfig.sip_info.crm_api_url
        .toString()
        .replace("{number}", callerId.value);
      window.open(crmUrl, "_blank")?.focus();
      break;
    case "SMS":
      dialogStore.setSmsPopUpVisibility(true);
      break;
    case "Forward":
      startCallTransfer();
      break;
    case "Add Info":
      dialogStore.setAddCustomerDialogVisibility(true);
      break;
    case "Add Notes":
      dialogStore.setAddNoteModalVisibility(true);
      break;
    case "Add Task":
      addTask();
      break;
    case "Add Lead":
      addLead();
      break;
    case "Call Histories":
      router.push({
        path: `/call-center/call-histories`,
        query: { customer_mobile_no: callerId.value },
        // to another window target="_blank"
      });

      break;
    case "Sale Info":
      dialogStore.setTeleSaleEditDialogVisibility(true);
      break;
  }
};
const showCallHistoryButton = computed(() => {
  if (callerId.value) {
    return true;
  } else {
    return false;
  }
});

const addTask = function () {
  tasksStore.setTaskableData({
    taskable_type: TaskableType.call_history,
    taskable_id: dialogStore.currentCallHistoryId,
  });
  tasksStore.setTaskProfileId(dialogStore?.currentProfile?.id);
  tasksStore.setCreateModalVisibility(true);
};

const addLead = function () {
  leadsStore.toggleCreateDialog();
  leadsStore.setLeadableData({
    leadable_type: LeadAbleType.call_history,
    leadable_id: dialogStore.currentCallHistoryId,
  });
};

const isInCall = function () {
  return (
    callStatus.value === "Ringing" || dialogStore.inCall === InCall.ONGOING
  );
};

const handleClose = (redial = false) => {
  if (isInCall() && !redial) {
    callService.endCall();
    dialogStore.setInCall(InCall.COMPLETED);
    return;
  }
  callService.endCall();
  dialogStore.setInCall(InCall.IDLE);

  callerId.value = "";
  callerQueue.value = "";
  dialogStore.setOngoingCallOrigin(CallOrigin.CC);
  dialogStore.setMaximizeButtonVisibility(false);
  dialogStore.setOngoingModalVisibility(false);
  dialogStore.resetCallDuration();
};

const confirmedCallback = () => {
  // auto-dialer 3
  console.log("confirm je call connect hoise");
  if (useAutoDialerStore().autoDialer) {
    dialogStore.setOngoingModalVisibility(true);
  }
  dialogStore.setInCall(InCall.ONGOING);
  showCallTime.value = true;
  setIntervalID = setInterval(setTime, 1000);

  const { phoneNumber, callHistoryId, queueName } = getCustomerUriInfo(
    callService.getCallerUri(),
  );
  callHisId.value = callHistoryId;
  dialogStore.setCurrentCallHistoryId(callHisId.value);

  callerId.value = getCleanedCustomerNumber(phoneNumber);
  callerQueue.value = queueName;
  dialogStore.setSmsNumber(callerId.value);

  callStatus.value = "In Call";
};
const call = function (phone: string) {
  handleClose(true);
  callService.makeCall(phone);
};
const completedCallback = () => {
  if (dialogStore.inCall === InCall.ONGOING) {
    dialogStore.setInCall(InCall.COMPLETED);
  }

  if (dialogStore.isIncoming) {
    dialogStore.setOngoingModalVisibility(true);
  }
  dialogStore.setIncomingModalVisibility(false);
  clearInterval(setIntervalID as unknown as string);

  resetData();
  if (dialogStore._showMaximizeButton) {
    dialogStore.setOngoingModalVisibility(true);
    dialogStore.setMaximizeButtonVisibility(false);
  }
  callStatus.value = "Last Call Duration " + lastCallSummary.value;
};

const resetData = () => {
  lastCallSummary.value = formatDuration(totalSeconds.value);
  selectedBtn.value = null;
  setIntervalID = null;
  showCallTime.value = false;
  isMuted.value = false;
  isPaused.value = false;
  callHisId.value = 0;
  dialogStore.resetLastCallAgent();
};

const progressCallback = (event: CallEventType) => {
  if (event.direction === "incoming") {
    if (callHistoriesStore.showAudioModal) {
      callHistoriesStore.setAudioModalVisibility(false);
    }
    inboxStore.pauseAllAudios();

    dialogStore.setIncomingModalVisibility(true);
    dialogStore.setIsIncoming(true);
  } else {
    // auto-dialer 2
    if (!useAutoDialerStore().autoDialer) {
      dialogStore.setOngoingModalVisibility(true);
    }
    dialogStore.setIsIncoming(false);
  }

  const { phoneNumber, queueName } = getCustomerUriInfo(
    callService.getCallerUri(),
  );
  callerId.value = getCleanedCustomerNumber(phoneNumber);
  callerQueue.value = queueName;
  dialogStore.setSmsNumber(callerId.value);
  dialogStore.setInCall(InCall.RINGING);
  callStatus.value = "Ringing";
};

const setTime = () => {
  dialogStore.incrementCallDuration(); // Increment call duration in dialogStore
};

const outgoingCallback = () => {
  // auto-dialer 1
  if (!useAutoDialerStore().autoDialer) {
    dialogStore.setOngoingModalVisibility(true);
  }
};

const { $listen, $unListen } = useNuxtApp();
onMounted(() => {
  $listen("confirmed", confirmedCallback);
  $listen("completed", completedCallback);
  $listen("progress", progressCallback);
  $listen("outgoing", outgoingCallback);
  $listen("reject", handleClose);
  $listen("hold", () => {
    if (!dialogStore._showOngoingCallDialog) return;
    selectedBtn.value = "Hold";
    isPaused.value = callService.togglePause();
  });

  $listen("add_task", () => {
    if (!dialogStore._showOngoingCallDialog) return;
    selectedBtn.value = "Add Task";
    addTask();
  });
  $listen("add_lead", () => {
    if (!dialogStore._showOngoingCallDialog) return;
    selectedBtn.value = "Add Lead";
    addLead();
  });
  $listen("add_note", () => {
    if (!dialogStore._showOngoingCallDialog) return;
    selectedBtn.value = "Add Notes";
    dialogStore.setAddNoteModalVisibility(true);
  });
});
const showRedialButton = computed(() => {
  return (
    dialogStore.inCall === InCall.COMPLETED ||
    dialogStore.inCall === InCall.MISSED ||
    dialogStore.inCall === InCall.FAILED ||
    dialogStore.inCall === InCall.REJECTED
  );
});

onUnmounted(() => {
  $unListen("confirmed", confirmedCallback);
  $unListen("completed", completedCallback);
  $unListen("progress", progressCallback);
  $unListen("outgoing", outgoingCallback);
  $unListen("reject", handleClose);
  $unListen("hold");
  $unListen("add_task");
  $unListen("add_lead");
  $unListen("add_note");
});
</script>

<template>
  <el-dialog
    v-model="dialogStore._showOngoingCallDialog"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :lock-scroll="false"
    :modal="false"
    :show-close="false"
    class="rounded-lg centerModal ongoing-call-dialog"
    draggable
    aling-center
    width="30%"
  >
    <div class="border rounded dark:border-dark-bluegray-border">
      <div
        class="bg-light-sec-purple dark:bg-light-purple-bar rounded rounded-b-none p-7 pt-10 place-items-center grid gap-2 relative"
      >
        <button
          class="bg-yellow-300 rounded-full w-fit text-white active:ring-2 active:ring-offset-2 active:ring-yellow-200 absolute top-2 left-2"
          @click="
            () => {
              dialogStore.setOngoingModalVisibility(false);
              dialogStore.setMaximizeButtonVisibility(true);
            }
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14" />
          </svg>
        </button>
        <span class="text-white text-xl">
          {{
            dialogStore.currentProfile
              ? dialogStore.currentProfile.name
              : "Unknown"
          }}
        </span>
        <div class="text-center grid gap-0">
          <span class="text-white text-xs">{{ callerId }}</span>
          <span v-if="callerQueue" class="text-white text-xs">
            Queue: {{ callerQueue }}
          </span>
          <span
            v-if="dialogStore.lastCallAgent"
            class="text-white/60 text-xs flex items-center justify-center gap-1 mt-0.5"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-3 h-3 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Last: {{ dialogStore.lastCallAgent.name }}
          </span>
        </div>

        <span class="text-white text-xl">
          <span v-if="dialogStore.inCall === InCall.ONGOING">
            {{ formatDuration(totalSeconds) }}
          </span>
          <span v-else-if="dialogStore.inCall === InCall.COMPLETED"
            >{{ lastCallSummary }}
          </span>
        </span>
        <span class="text-white text-lg">
          <span v-if="dialogStore.inCall === InCall.IDLE">Calling...</span>
          <span v-else-if="dialogStore.inCall === InCall.ONGOING"
            >Call Ongoing...</span
          >
          <span v-else-if="dialogStore.inCall === InCall.COMPLETED"
            >Call Ended</span
          >
          <span v-else-if="dialogStore.inCall === InCall.REJECTED"
            >Call Rejected</span
          >
          <span v-else-if="dialogStore.inCall === InCall.MISSED"
            >Call Missed</span
          >
          <span v-else-if="dialogStore.inCall === InCall.FAILED"
            >Call Failed</span
          >
        </span>
      </div>

      <div
        class="bg-white flex flex-col gap-2 rounded rounded-b-none p-10 dark:bg-[#191D23]"
      >
        <!--									<span class="text-gray-500 text-xs text-center">Call received (+880) 01732986106</span>-->
        <div v-if="!showDialpad" class="grid grid-cols-3 gap-4 gap-x-5">
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Mute' as CallOptionName)"
          >
            <Svgs
              :fill="selectedBtn == 'Mute' ? `currentColor` : 'none'"
              :name="selectedBtn == 'Mute' ? `solid-microphone` : 'microphone'"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Mute" }}
            </span>
          </div>
          <!-- end Mute -->
          <!-- begin Hold -->
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Hold' as CallOptionName)"
          >
            <Svgs
              :fill="selectedBtn == 'Hold' ? `currentColor` : 'none'"
              :name="selectedBtn == 'Hold' ? `solid-pause` : 'pause'"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Hold" }}
            </span>
          </div>
          <!-- end Hold -->
          <!-- begin CRM -->
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            :class="{
              'cursor-not-allowed opacity-50 pointer-events-none ': !!(
                !configStore.globalConfig.sip_info.crm_api_url || !callerId
              ),
            }"
            @click="handleCallOption('CRM' as CallOptionName)"
          >
            <Svgs
              :fill="selectedBtn == 'CRM' ? `currentColor` : 'none'"
              :name="selectedBtn == 'CRM' ? `solid-speaker` : 'speaker'"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "CRM" }}
            </span>
          </div>
          <!-- end CRM -->
          <!-- begin SMS -->
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('SMS' as CallOptionName)"
          >
            <Svgs
              :fill="selectedBtn == 'SMS' ? `currentColor` : 'none'"
              :name="selectedBtn == 'SMS' ? `solid-sms` : 'sms'"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "SMS" }}
            </span>
          </div>
          <!-- end SMS -->
          <!-- begin Forward -->
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            :class="{
              'cursor-not-allowed opacity-50 pointer-events-none ':
                !dialogStore.isIncoming,
            }"
            @click="handleCallOption('Forward' as CallOptionName)"
          >
            <Svgs
              :fill="selectedBtn == 'Forward' ? `currentColor` : 'none'"
              :name="
                selectedBtn == 'Forward'
                  ? `solid-phone-forward`
                  : 'phone-forward'
              "
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Forward" }}
            </span>
          </div>
          <!-- end Forward -->
          <!-- begin Add Info -->

          <NuxtLink
            v-if="dialogStore.currentProfile"
            :to="`/profiles/${dialogStore.currentProfile.id}`"
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
          >
            <Svgs
              name="user"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400"> Profile </span>
          </NuxtLink>
          <!-- end Add Info -->
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Add Notes' as CallOptionName)"
          >
            <Svgs
              :fill="dialogStore.showAddNotes ? `currentColor` : 'none'"
              :name="dialogStore.showAddNotes ? `notes` : 'notes'"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Call Info" }}
            </span>
          </div>
          <div
            v-if="showCallHistoryButton"
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Add Task' as CallOptionName)"
          >
            <Svgs
              fill="none"
              name="task"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Add Task" }}
            </span>
          </div>
          <div
            v-if="showCallHistoryButton"
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Add Lead' as CallOptionName)"
          >
            <Svgs
              fill="none"
              name="user-plus"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Add Lead" }}
            </span>
          </div>
          <div
            v-if="showCallHistoryButton"
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            @click="handleCallOption('Call Histories' as CallOptionName)"
          >
            <Svgs
              fill="none"
              name="clock"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Call Histories" }}
            </span>
          </div>
          <div
            class="hover:cursor-pointer grid gap-2 place-items-center rounded-full active:ring-2 active:ring-offset-2 active:ring-light-purple"
            :class="{
              'cursor-not-allowed opacity-50 pointer-events-none':
                dialogStore.inCall !== InCall.ONGOING,
            }"
            @click="openDialpad()"
          >
            <Svgs
              fill="currentColor"
              name="dialpad"
              class="w-[30px] text-light-sec-purple dark:text-white"
              stroke-width="1"
            />
            <span class="text-xs text-gray-400">
              {{ "Dialpad" }}
            </span>
          </div>
        </div>

        <div v-else class="grid gap-3">
          <div class="flex items-center justify-between gap-3 min-h-[28px]">
            <el-tooltip placement="top" effect="light">
              <template #content>
                <div class="max-w-[220px] text-xs leading-5">
                  Sends keypad tones to the other side. Use it to move through
                  an automated phone menu, like "press 1 for sales". It will not
                  dial a new number.
                </div>
              </template>
              <button
                class="shrink-0 rounded-full p-1 cursor-help text-light-sec-purple dark:text-white hover:bg-[#F2F5FD] dark:hover:bg-[#303743]"
              >
                <Svgs name="info" class="w-5" />
              </button>
            </el-tooltip>
            <span
              class="flex-1 min-w-0 text-center text-lg tracking-[0.2em] truncate text-light-sec-purple dark:text-white"
            >
              {{ dtmfLog }}
            </span>
            <button
              class="shrink-0 rounded-full p-1 text-light-sec-purple dark:text-white hover:bg-[#F2F5FD] dark:hover:bg-[#303743] active:ring-2 active:ring-light-purple"
              @click="closeDialpad()"
            >
              <Svgs name="close" class="w-5" stroke-width="2" />
            </button>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <button
              v-for="key in dtmfKeys"
              :key="key"
              class="shadow-[#5A55CE66]/[.5] text-[#5A55CE] p-2 2xl:p-3 text-xl 2xl:text-3xl active:ring-2 rounded-full active:ring-light-purple dark:active:ring-light-purple-bar dark:text-white bg-[#F2F5FD] dark:bg-[#303743]"
              @click="sendDtmf(key)"
            >
              {{ key }}
            </button>
          </div>
        </div>

        <div class="place-self-center flex gap-2">
          <button
            class="bg-red-500 rounded-full p-3 w-fit text-white mt-10 active:ring-2 active:ring-offset-2 active:ring-red-500"
            @click="handleClose(false)"
          >
            <Svgs
              :class="{ 'rotate-[134deg]': isInCall() }"
              :name="isInCall() ? 'solid-phone' : 'close'"
              class="w-6"
              fill="currentColor"
            />
          </button>
          <el-tooltip
            v-if="showCallHistoryButton"
            content="Redial"
            placement="top"
          >
            <button
              class="bg-green-500 rounded-full p-3 w-fit text-white mt-10 active:ring-2 active:ring-offset-2 active:ring-green-500"
              @click="call(callerId)"
            >
              <Svgs class="w-6" name="retry" />
            </button>
          </el-tooltip>
        </div>
      </div>
    </div>
  </el-dialog>
  <!--Add Info start-->
  <!--Add Info end-->
  <!--Add Notes start-->
  <CallAddNote></CallAddNote>
  <CallSaleInfo></CallSaleInfo>
  <AddCustomer :key="callerId" :phone="callerId" />
  <!--Add Notes end-->
</template>

<style scoped></style>
